import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, ShieldCheck, X } from 'lucide-react';
import { adminAPI as baseAdminAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useOperationsAPI } from '../context/useOperationsAPI';

type Decision = 'use_existing' | 'retry_unstarted' | 'exclude';
type Review = {
  rowId: string; batchId: string; rowNumber: number; status: string; expectedFingerprint: string; classification: string;
  candidate: { id: string; name: string } | null;
  originals: Array<{ rowId: string; batchId: string; status: string; batchStatus: string }>;
  actions: Array<{ decision: Decision; enabled: boolean; reason: string }>;
  evidence: { provider: string; complete: boolean; observedAt: string };
  reviewed?: { decision: Decision; reason: string; createdAt: string };
  notes: string[];
};
const labels: Record<Decision, string> = {
  use_existing: 'Acknowledge existing customer', retry_unstarted: 'Import this unstarted row', exclude: 'Exclude from this upload',
};

export function CustomerImportReviewPanel({ rowId, onResolved, onClose }: {
  rowId: string; onResolved: () => void; onClose: () => void;
}) {
  const { user, operationsCompanyId, operationsScope } = useAuth();
  const adminAPI = useOperationsAPI(baseAdminAPI);
  const key = JSON.stringify([window.location.host, user?.id, user?.company_id, operationsCompanyId, operationsScope, rowId]);
  const [loaded, setLoaded] = useState<{ key: string; data: Review } | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [form, setForm] = useState({ key, decision: '' as Decision | '', reason: '', confirm: false });
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [message, setMessage] = useState<{ key: string; text: string } | null>(null);
  const submitting = useRef(false);
  const mounted = useRef(true);
  const current = useRef(key);
  current.current = key;
  const data = loaded?.key === key ? loaded.data : null;
  const draft = form.key === key ? form : { key, decision: '' as Decision | '', reason: '', confirm: false };
  const busy = busyKey === key;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    let cancelled = false;
    setLoaded(null); setMessage(null);
    setForm({ key, decision: '', reason: '', confirm: false });
    adminAPI.getCustomerImportReview(rowId).then(response => {
      if (!cancelled && current.current === key && response.data.rowId === rowId) setLoaded({ key, data: response.data });
    }).catch(() => {
      if (!cancelled && current.current === key) setMessage({ key, text: 'Row review could not be loaded. No decision has been applied.' });
    });
    return () => { cancelled = true; };
  }, [adminAPI, key, rowId, refresh]);

  const apply = async () => {
    const action = data?.actions.find(item => item.decision === draft.decision);
    if (!data || !draft.decision || !action?.enabled || !draft.confirm || draft.reason.trim().length < 8 || submitting.current) return;
    submitting.current = true;
    setBusyKey(key); setMessage(null);
    try {
      const response = await adminAPI.reviewCustomerImportRow(rowId, {
        decision: draft.decision, expectedFingerprint: data.expectedFingerprint, reason: draft.reason.trim(), confirm: true,
      });
      if (!mounted.current || current.current !== key) return;
      setMessage({ key, text: `Review recorded. Row status: ${String(response.data.status || 'refresh required').replace(/_/g, ' ')}.` });
      setLoaded(null); setRefresh(value => value + 1); onResolved();
    } catch {
      if (mounted.current && current.current === key) {
        setLoaded(null);
        setMessage({ key, text: 'The outcome is unconfirmed. Refresh this row before attempting another decision.' });
      }
    } finally { submitting.current = false; if (mounted.current && current.current === key) setBusyKey(null); }
  };

  return <section aria-label="Import row review" className="border-t border-outline-variant mt-4 pt-4 space-y-4">
    <div className="flex items-center justify-between gap-3">
      <h3 className="font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" />{data ? `Review row ${data.rowNumber}` : 'Row review'}</h3>
      <div className="flex gap-2">
        <button type="button" title="Refresh row review" aria-label="Refresh row review" className="btn-secondary" disabled={busy}
          onClick={() => setRefresh(value => value + 1)}><RefreshCw size={16} aria-hidden="true" /></button>
        <button type="button" title="Close row review" aria-label="Close row review" className="btn-secondary" disabled={busy} onClick={onClose}><X size={16} aria-hidden="true" /></button>
      </div>
    </div>
    {message?.key === key && <p role="status">{message.text}</p>}
    {!data && !message && <p role="status">Loading row review...</p>}
    {data && <>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div><dt className="font-medium">Row status</dt><dd>{data.status.replace(/_/g, ' ')}</dd></div>
        <div><dt className="font-medium">Stripe evidence</dt><dd>{data.evidence.provider.replace(/_/g, ' ')}{!data.evidence.complete && ' (incomplete)'}</dd></div>
        {data.candidate && <div><dt className="font-medium">Existing customer</dt><dd>{data.candidate.name}<span className="block break-all font-mono text-xs">{data.candidate.id}</span></dd></div>}
        <div><dt className="font-medium">Checked</dt><dd>{new Date(data.evidence.observedAt).toLocaleString()}</dd></div>
      </dl>
      {data.notes.map(note => <p key={note} className="text-sm">{note}</p>)}
      {data.originals.length > 0 && <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr><th className="text-left">Original batch</th><th className="text-left">Batch state</th><th className="text-left">Row state</th></tr></thead>
        <tbody>{data.originals.map(original => <tr key={original.rowId}><td className="py-2 pr-3 break-all font-mono text-xs">{original.batchId}</td><td className="pr-3">{original.batchStatus.replace(/_/g, ' ')}</td><td>{original.status.replace(/_/g, ' ')}</td></tr>)}</tbody></table></div>}
      {data.reviewed ? <p>Recorded decision: {labels[data.reviewed.decision]}. {data.reviewed.reason}</p> : <>
        <label className="block text-sm font-medium">Decision<select aria-label="Decision" className="w-full mt-1 rounded border border-outline-variant p-2" value={draft.decision} disabled={busy}
          onChange={event => setForm({ ...draft, decision: event.target.value as Decision | '', confirm: false })}>
          <option value="">Select a decision</option>{data.actions.map(action => <option key={action.decision} value={action.decision} disabled={!action.enabled}>{labels[action.decision]}{!action.enabled ? ' (unavailable)' : ''}</option>)}
        </select></label>
        <ul className="text-sm space-y-2">{data.actions.map(action => <li key={action.decision}><strong>{labels[action.decision]}: </strong>{action.reason}{!action.enabled && ' Not currently available.'}</li>)}</ul>
        <label className="block text-sm font-medium">Review reason<textarea className="w-full mt-1 rounded border border-outline-variant p-2" rows={3} maxLength={500} value={draft.reason} disabled={busy}
          onChange={event => setForm({ ...draft, reason: event.target.value })} /></label>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={draft.confirm} disabled={busy || !draft.decision}
          onChange={event => setForm({ ...draft, confirm: event.target.checked })} />I have reviewed this row and approve the selected decision for this upload.</label>
        <button type="button" className="btn-primary" disabled={busy || !draft.decision || !draft.confirm || draft.reason.trim().length < 8 || !data.actions.some(action => action.decision === draft.decision && action.enabled)} onClick={() => void apply()}>
          {busy ? 'Recording review...' : 'Apply reviewed decision'}
        </button>
      </>}
    </>}
  </section>;
}
